import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

const AboutPage = () => {
  // SEO Configuration
  useSEO({
    title: 'About PURECODE Ltd - Warrington IT Consultancy Team & Company History',
    description: 'Learn about PURECODE Ltd, a boutique IT consultancy in Warrington with 8+ years of experience serving banking, fintech, and betting industries. Meet our team and discover our mission to empower businesses with cutting-edge technology.',
    keywords: 'about PURECODE, IT consultancy Warrington, fintech company history, technology team, Warrington tech company',
    canonical: 'https://purecode.uk/about',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'About', url: '/about' }
        ]),
        {
          '@type': 'AboutPage',
          '@id': 'https://purecode.uk/about#aboutpage',
          url: 'https://purecode.uk/about',
          name: 'About PURECODE Ltd',
          description: 'Learn about PURECODE Ltd, our mission, vision, and team',
          mainEntity: {
            '@id': 'https://purecode.uk/#organization'
          }
        }
      ]
    }
  });

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We stay at the forefront of technology, constantly exploring new solutions to solve complex business challenges.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Reliability',
      description: 'Our clients trust us to deliver robust, secure, and scalable solutions that perform when it matters most.'
    },
    {
      icon: 'ri-team-line',
      title: 'Collaboration',
      description: 'We work closely with our clients as partners, ensuring every solution aligns with their business objectives.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Excellence',
      description: 'We are committed to delivering exceptional quality in every project, exceeding expectations consistently.'
    }
  ];

  const milestones = [
    { year: '2017', event: 'Journey started with a vision to democratize fintech technology for small businesses' },
    { year: '2018', event: 'Reached 15+ successful project implementations for growing companies' },
    { year: '2020', event: 'Expanded to serve emerging fintech startups and betting platforms' },
    { year: '2022', event: 'Launched specialized AI solutions for financial services automation' },
    { year: '2023', event: 'Established strategic partnerships with leading cloud providers and fintech platforms' },
    { year: '2024', event: 'Celebrating 7 years of innovation and 50+ completed projects' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 text-white py-24"
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(8, 145, 178, 0.85)), url('https://public.readdy.ai/ai/img_res/e478b7f8d48cf78e173ab97ac6047e0e.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-cyan-900/70 to-transparent"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-6xl mb-6">
            About PURECODE
          </h1>
          <p className="text-xl leading-8 text-teal-100 max-w-3xl mx-auto">
            We are a boutique team of passionate technologists dedicated to transforming small businesses 
            through innovative fintech solutions and cutting-edge technology.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 mb-6">
                To empower small and growing businesses with cutting-edge fintech solutions that drive growth, 
                efficiency, and innovation. We believe that every organization deserves access to world-class 
                financial technology systems and AI capabilities.
              </p>
              <p className="text-lg text-gray-600">
                Through our expertise in fintech development, systems design, and AI product creation, we help our 
                clients stay competitive in an increasingly digital financial landscape.
              </p>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Vision</h2>
              <p className="text-lg text-gray-600 mb-6">
                To be the leading boutique IT consultancy that bridges the gap between complex fintech technology and 
                practical business solutions. We envision a future where AI and advanced financial systems 
                are seamlessly integrated into every business operation.
              </p>
              <p className="text-lg text-gray-600">
                Our goal is to make sophisticated fintech technology accessible, understandable, and profitable 
                for businesses across the financial services, betting, and emerging fintech sectors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gradient-to-r from-teal-50 to-cyan-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Our Core Values
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                  <i className={`${value.icon} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Key Milestones
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Important moments in our company's growth and evolution
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-teal-200"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white p-6 rounded-lg shadow-lg">
                      <div className="text-2xl font-bold text-teal-600 mb-2">{milestone.year}</div>
                      <p className="text-gray-700">{milestone.event}</p>
                    </div>
                  </div>
                  <div className="relative z-10 w-4 h-4 bg-teal-600 rounded-full border-4 border-white shadow-lg"></div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-6">
            Ready to Work with Us?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Join growing businesses who trust PURECODE to deliver quality fintech solutions and drive digital transformation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="rounded-lg bg-white px-6 py-3 text-lg font-semibold text-teal-600 shadow-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
            >
              Start Your Project
            </Link>
            <Link
              to="/careers"
              className="rounded-lg border-2 border-teal-300 px-6 py-3 text-lg font-semibold text-white hover:bg-teal-500/20 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Join Our Team
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
